import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, extractError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0); // 0: OTP input, 1: Password reset

  const submit = async (e) => {
    e.preventDefault();
    
    if (step === 0) {
      // Validate OTP
      if (!otp || otp.length !== 6) {
        toast.error("Please enter a valid 6-digit code");
        return;
      }
      setStep(1);
    } else {
      // Reset password
      if (!password || password.length < 8) {
        toast.error("Password must be at least 8 characters");
        return;
      }
      if (password !== confirmPassword) {
        toast.error("Passwords do not match");
        return;
      }
      
      setLoading(true);
      try {
        await api.post("/auth/reset-password", { token: otp, password });
        toast.success("Password updated successfully");
        navigate("/login");
      } catch (err) {
        toast.error(extractError(err));
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <Link to="/" className="font-serif text-3xl tracking-tight text-[#1A1A1A]">EventFlow</Link>
        
        {step === 0 ? (
          <>
            <h1 className="font-serif text-4xl tracking-tighter mt-8">Enter reset code</h1>
            <p className="text-sm text-zinc-600 mt-2">Check your WhatsApp for the 6-digit code we sent to your mobile number.</p>
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div>
                <Label htmlFor="otp">Reset code</Label>
                <Input 
                  id="otp" 
                  required 
                  inputMode="numeric" 
                  maxLength={6} 
                  value={otp} 
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} 
                  className="mt-1.5 h-11 text-center text-2xl tracking-widest" 
                  placeholder="000000"
                  data-testid="reset-otp-input"
                />
                <div className="text-xs text-zinc-500 mt-1">Enter the 6-digit code</div>
              </div>
              <Button 
                type="submit" 
                disabled={loading} 
                className="w-full h-11 rounded-full bg-[#D85C38] hover:bg-[#B84A2A] text-white"
                data-testid="reset-otp-submit-btn"
              >
                {loading ? "Verifying…" : "Verify code"}
              </Button>
            </form>
          </>
        ) : (
          <>
            <h1 className="font-serif text-4xl tracking-tighter mt-8">Set a new password</h1>
            <p className="text-sm text-zinc-600 mt-2">Choose a strong password to continue.</p>
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div>
                <Label htmlFor="password">New password</Label>
                <Input 
                  id="password" 
                  required 
                  type="password" 
                  minLength={8} 
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)} 
                  className="mt-1.5 h-11"
                  placeholder="Minimum 8 characters"
                  data-testid="reset-password-input"
                />
                <div className="text-xs text-zinc-500 mt-1">Minimum 8 characters</div>
              </div>
              <div>
                <Label htmlFor="confirm-password">Confirm password</Label>
                <Input 
                  id="confirm-password" 
                  required 
                  type="password" 
                  minLength={8} 
                  value={confirmPassword} 
                  onChange={(e) => setConfirmPassword(e.target.value)} 
                  className="mt-1.5 h-11"
                  placeholder="Re-enter password"
                  data-testid="reset-confirm-password-input"
                />
              </div>
              <Button 
                type="submit" 
                disabled={loading} 
                className="w-full h-11 rounded-full bg-[#1A1A1A] text-white hover:bg-black"
                data-testid="reset-password-submit-btn"
              >
                {loading ? "Updating…" : "Update password"}
              </Button>
            </form>
            <button 
              onClick={() => setStep(0)}
              className="block mt-4 text-sm text-zinc-600 hover:text-[#1A1A1A]"
              data-testid="reset-back-btn"
            >
              ← Back to enter code
            </button>
          </>
        )}

        <Link to="/login" className="block mt-8 text-sm text-zinc-600 hover:text-[#1A1A1A]">← Back to sign in</Link>
      </div>
    </div>
  );
}
